import DeactivateOrganizationWorkflow from '@Application/Organizations/Workflows/Commands/DeactivateOrganizationWorkflow';
import RestoreOrganizationWorkflow from '@Application/Organizations/Workflows/Commands/RestoreOrganizationWorkflow';
import deactivateOrganizationWorkflow from '@Application/Organizations/Workflows/deactivateOrganizationWorkflow';
import restoreOrganizationWorkflow from '@Application/Organizations/Workflows/restoreOrganizationWorkflow';
import { PermissionEnum } from '@Application/Security/Roles';
import ignoreHandledError from '@UI/Helpers/ignoreHandledError';
import CheckboxTile from '@UI/Pages/Administration/DocumentGroups/DocumentGroupEdit/Shared/CheckboxTile';
import HintText from '@Guidelines/Component/HintText';
import SectionCard from '@Guidelines/Component/SectionCard';
import useHasPermission from '@UI/Providers/PermissionProvider/useHasPermission';
import * as React from 'react';
import { useDispatch } from '@verclam/synapse';
import { ConfirmDialog } from '@verclam/ui-kit';

type Props = {
	readonly organizationId: number;
	readonly workflowKey: string;
	readonly workflowLabel: string;
	readonly isActive: boolean;
	/** What the transcription covers, in the plural. */
	readonly kindLabel: 'entretiens' | 'notes';
};

const DEACTIVATED_HINTS: Readonly<Record<Props['kindLabel'], string>> = Object.freeze({
	entretiens: 'Désactivée, les entretiens restent enregistrés mais ne sont pas transcrits.',
	notes: 'Désactivée, les notes restent enregistrées mais ne sont pas transcrites.',
});

const DEACTIVATION_WARNINGS: Readonly<Record<Props['kindLabel'], string>> = Object.freeze({
	entretiens: 'Les entretiens ajoutés ensuite resteront sans transcription',
	notes: 'Les notes ajoutées ensuite resteront sans transcription',
});

/**
 * Leaf block: the switch that turns the transcription on or off, dispatched at once. Turning it off
 * asks first, since the files recorded afterwards stay untranscribed; turning it on does not.
 */
const ActivationSection: React.FC<Props> = ({
	organizationId,
	workflowKey,
	workflowLabel,
	isActive,
	kindLabel,
}) => {
	const dispatch = useDispatch();
	const canWrite = useHasPermission(PermissionEnum.ORGANIZATION_WRITE);
	const [isConfirmVisible, setIsConfirmVisible] = React.useState(false);

	const handleActiveChange = React.useCallback(
		(checked: boolean) => {
			if (!checked) {
				setIsConfirmVisible(true);
				return;
			}

			dispatch(
				restoreOrganizationWorkflow(new RestoreOrganizationWorkflow(organizationId, workflowKey)),
			).catch(ignoreHandledError);
		},
		[dispatch, organizationId, workflowKey],
	);

	const handleConfirmHide = React.useCallback(() => {
		setIsConfirmVisible(false);
	}, []);

	const handleConfirmDeactivation = React.useCallback(() => {
		dispatch(
			deactivateOrganizationWorkflow(new DeactivateOrganizationWorkflow(organizationId, workflowKey)),
		).catch(ignoreHandledError);
		setIsConfirmVisible(false);
	}, [dispatch, organizationId, workflowKey]);

	return (
		<SectionCard>
			<CheckboxTile
				name="isActive"
				label="Transcription automatique activée"
				checked={isActive}
				disabled={!canWrite}
				onChange={handleActiveChange}
			/>
			<HintText>{DEACTIVATED_HINTS[kindLabel]}</HintText>
			<ConfirmDialog
				blockScroll
				closable
				draggable={false}
				visible={isConfirmVisible}
				onHide={handleConfirmHide}
				header="Désactiver la transcription ?"
				message={
					<p>
						{DEACTIVATION_WARNINGS[kindLabel]} et ne pourront pas servir à générer des documents,
						tant que « {workflowLabel} » reste désactivée.
					</p>
				}
				acceptSeverity="danger"
				acceptLabel="Désactiver"
				rejectLabel="Annuler"
				accept={handleConfirmDeactivation}
				reject={handleConfirmHide}
			/>
		</SectionCard>
	);
};

export default ActivationSection;
